import { useController } from 'react-hook-form';
import { Autocomplete, Checkbox, TextField, useForkRef } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useFormError } from './FormErrorProvider';
import { forwardRef } from 'react';
import { useTransform } from './useTransform';
import { propertyExists } from './utils';

const AutocompleteElement = forwardRef(function AutocompleteElement(
  props,
  ref
) {
  const {
    textFieldProps,
    autocompleteProps,
    name,
    control,
    options,
    loading,
    showCheckbox,
    rules,
    loadingIndicator,
    required,
    multiple,
    label,
    parseError,
    transform,
    matchId
  } = props;

  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const validationRules = {
    ...rules,
    ...(required && {
      required: rules?.required || 'This field is required'
    })
  };

  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control,
    disabled: autocompleteProps?.disabled,
    rules: validationRules
  });

  const getOptionLabel = option => {
    if (typeof autocompleteProps?.getOptionLabel === 'function') {
      return autocompleteProps.getOptionLabel(option);
    }
    if (propertyExists(option, 'label')) {
      return `${option?.label}`;
    }
    return `${option}`;
  };

  const isOptionEqualToValue = (option, value) => {
    if (typeof autocompleteProps?.isOptionEqualToValue == 'function') {
      return autocompleteProps.isOptionEqualToValue(option, value);
    }
    const optionKey = propertyExists(option, 'id') ? option.id : option;
    const valueKey = propertyExists(value, 'id') ? value.id : value;
    return optionKey === valueKey;
  };

  const matchOptionByValue = currentValue => {
    return options.find(option => {
      if (matchId && propertyExists(option, 'id')) {
        return option.id === currentValue;
      }
      return isOptionEqualToValue(option, currentValue);
    });
  };

  const { value, onChange } = useTransform({
    value: field.value,
    onChange: field.onChange,
    transform: {
      input:
        typeof transform?.input === 'function'
          ? transform.input
          : newValue => {
            return multiple
              ? (Array.isArray(newValue) ? newValue : []).map(
                matchOptionByValue
              )
              : matchOptionByValue(newValue) ?? null;
          },
      output:
        typeof transform?.output === 'function'
          ? transform.output
          : (_event, newValue) => {
            if (multiple) {
              const newValues = Array.isArray(newValue) ? newValue : [];
              return matchId
                ? newValues.map(currentValue =>
                  propertyExists(currentValue, 'id')
                    ? currentValue.id
                    : currentValue
                )
                : newValues;
            }
            return matchId && propertyExists(newValue, 'id')
              ? newValue.id
              : newValue;
          }
    }
  });

  const handleInputRef = useForkRef(field.ref, textFieldProps?.inputRef);

  const loadingElement = loadingIndicator || (
    <CircularProgress color="inherit" size={20} />
  );

  return (
    <Autocomplete
      {...autocompleteProps}
      value={value}
      loading={loading}
      multiple={multiple}
      options={options}
      disableCloseOnSelect={
        typeof autocompleteProps?.disableCloseOnSelect === 'boolean'
          ? autocompleteProps.disableCloseOnSelect
          : !!multiple
      }
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      onChange={(event, newValue, reason, details) => {
        onChange(event, newValue, reason, details);
        if (autocompleteProps?.onChange) {
          autocompleteProps.onChange(event, newValue, reason, details);
        }
      }}
      ref={ref}
      renderOption={
        autocompleteProps?.renderOption ??
        (showCheckbox
          ? (props, option, { selected }) => {
            return (
              <li {...props}>
                <Checkbox sx={{ marginRight: 1 }} checked={selected} />
                {getOptionLabel(option)}
              </li>
            );
          }
          : undefined)
      }
      onBlur={event => {
        field.onBlur();
        if (typeof autocompleteProps?.onBlur === 'function') {
          autocompleteProps.onBlur(event);
        }
      }}
      renderInput={params => (
        <TextField
          name={name}
          required={rules?.required ? true : required}
          label={label}
          {...textFieldProps}
          {...params}
          error={!!error || !!textFieldProps?.error}
          InputLabelProps={{
            ...params.InputLabelProps,
            ...textFieldProps?.InputLabelProps
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? loadingElement : null}
                {params.InputProps.endAdornment}
              </>
            ),
            ...textFieldProps?.InputProps
          }}
          inputProps={{
            ...params.inputProps,
            ...textFieldProps?.inputProps
          }}
          helperText={
            error
              ? typeof customErrorFn === 'function'
                ? customErrorFn(error)
                : error.message
              : textFieldProps?.helperText
          }
          inputRef={handleInputRef}
        />
      )}
    />
  );
});
AutocompleteElement.displayName = 'AutocompleteElement';
export default AutocompleteElement;
